import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    getSecurityDevices, getDeviceOverview, searchOverviewDevices,
} from '../api/internal/usersInternalApi';
import {
    Card, PageHead, Button, IconButton, Pill, PillDot, Input, Select,
    EmptyState, Spinner, SpinnerContainer, Notice, DataTable, TableScroll, FieldLabel,
} from '../components/ui';
import {
    FilterBar, ClickableRow, Muted, Pager, SuggestionsList, SuggestionItem, SourceTags,
} from '../components/security/security.styled';
import { formatDate } from '../components/security/severity';
import DeviceOverviewView from '../components/security/deviceOverviewView';

const PAGE_SIZE = 50;

const HEALTH_TONE = {
    Active: 'ok',
    Inactive: 'neutral',
    ImpairedCommunication: 'warn',
    NoSensorData: 'warn',
    NoSensorDataImpairedCommunication: 'warn',
};

const RISK_TONE = { High: 'danger', Medium: 'warn', Low: 'accent', None: 'neutral' };

// Les trois inventaires qui peuvent connaître un appareil, en initiales.
const SOURCE_BADGES = [
    { key: 'entra', letter: 'E', name: 'Entra ID' },
    { key: 'intune', letter: 'I', name: 'Intune' },
    { key: 'defender', letter: 'D', name: 'Defender' },
];

function SourceBadges({ sources }) {
    if (!sources) return <Muted>—</Muted>;

    // Sans identifiant Entra sur la fiche Defender, le rapprochement est
    // impossible : un badge creux ne dit alors pas « absent » mais « inconnu ».
    // Le distinguer évite de faire chercher dans Entra un poste qui y est
    // peut-être, mais que rien ne permet de relier.
    const label = (key, name) => {
        if (sources[key]) return `${name} : présent`;
        if (key !== 'defender' && !sources.matchable) {
            return `${name} : inconnu — la fiche Defender ne porte pas d'identifiant Entra, le rapprochement est impossible`;
        }
        return `${name} : absent`;
    };

    return (
        <SourceTags>
            {SOURCE_BADGES.map(({ key, letter, name }) => (
                <span
                    key={key}
                    className={sources[key] ? 'on' : undefined}
                    title={label(key, name)}
                >
                    {sources[key] || key === 'defender' || sources.matchable ? letter : '?'}
                </span>
            ))}
        </SourceTags>
    );
}

// Fiche complète d'un appareil. L'ancienne page « Vue 360° » est devenue ce
// détail : une seule fiche appareil dans l'application, atteignable soit par le
// parcours de la liste, soit par la recherche inter-inventaires.
function DeviceDetail({ deviceKey, onBack }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const controller = new AbortController();
        setLoading(true);
        setError(null);
        getDeviceOverview(deviceKey, controller.signal)
            .then((res) => { if (res) setData(res); })
            .catch((e) => setError(
                e.response?.status === 404
                    ? `Aucun appareil ne correspond à « ${deviceKey} » dans Entra ni dans Defender.`
                    : 'Chargement impossible.'
            ))
            .finally(() => setLoading(false));
        return () => controller.abort();
    }, [deviceKey]);

    return (
        <>
            <PageHead>
                <div>
                    <h2>{data?.identity.entraName || data?.identity.defenderName || 'Appareil'}</h2>
                    <p>Identité, propriétaire, posture Defender, vulnérabilités et conformité Intune.</p>
                </div>
                <Button $variant="secondary" onClick={onBack}>
                    <i className="bx bx-arrow-back" />
                    Retour à la liste
                </Button>
            </PageHead>

            {loading && <Card><SpinnerContainer><Spinner /></SpinnerContainer></Card>}

            {error && (
                <Card>
                    <Notice $tone="warn"><i className="bx bx-error" />{error}</Notice>
                </Card>
            )}

            {data && !loading && <DeviceOverviewView data={data} />}
        </>
    );
}

export default function SecurityDevices() {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedDevice = searchParams.get('device');

    const [search, setSearch] = useState('');
    const [healthStatus, setHealthStatus] = useState('');
    const [riskScore, setRiskScore] = useState('');
    // Les appareils exclus du suivi Defender sont masqués par défaut : 415
    // fiches sur 2 016 au dernier relevé, dont 173 sans même un nom de poste.
    const [includeExcluded, setIncludeExcluded] = useState(false);
    const [page, setPage] = useState(1);

    const [result, setResult] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Suggestions inter-inventaires : le tableau ci-dessous est bâti sur
    // l'inventaire Defender, alors que 849 appareils Entra actifs n'y sont pas
    // onboardés. La recherche interroge les deux — sans quoi ces postes
    // resteraient inatteignables depuis cette page.
    const [suggestions, setSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);

    const fetchPage = useCallback((signal) => {
        setLoading(true);
        setError(null);
        return getSecurityDevices({
            page, limit: PAGE_SIZE, search, healthStatus, riskScore,
            includeExcluded: includeExcluded ? 'true' : undefined,
        }, signal)
            .then((data) => { if (data) setResult(data); })
            .catch(() => setError('Chargement des appareils impossible.'))
            .finally(() => setLoading(false));
    }, [page, search, healthStatus, riskScore, includeExcluded]);

    useEffect(() => {
        if (selectedDevice) return undefined;
        const controller = new AbortController();
        const timer = setTimeout(() => fetchPage(controller.signal), search ? 300 : 0);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [fetchPage, selectedDevice, search]);

    useEffect(() => {
        if (selectedDevice || search.trim().length < 2) { setSuggestions([]); return undefined; }
        const controller = new AbortController();
        const timer = setTimeout(() => {
            searchOverviewDevices(search.trim(), controller.signal)
                .then((res) => { if (res) { setSuggestions(res.results); setShowSuggestions(true); } })
                .catch(() => setSuggestions([]));
        }, 300);
        return () => { clearTimeout(timer); controller.abort(); };
    }, [search, selectedDevice]);

    const updateFilter = (setter) => (value) => { setter(value); setPage(1); };
    const openDevice = (key) => {
        setShowSuggestions(false);
        setSearchParams({ device: key });
    };

    if (selectedDevice) {
        return (
            <DeviceDetail
                deviceKey={selectedDevice}
                onBack={() => setSearchParams({}, { replace: true })}
            />
        );
    }

    const pagination = result?.pagination;
    // Ces appareils ne peuvent pas figurer dans le tableau : il vient de
    // l'inventaire Defender, dont ils sont absents. On les propose à part.
    const outsideDefender = suggestions.filter((s) => !s.inDefender);

    return (
        <>
            <PageHead>
                <div>
                    <h2>Appareils</h2>
                    <p>
                        Parc surveillé par Defender. Cliquez sur un appareil pour sa fiche complète —
                        identité, propriétaire, vulnérabilités et conformité Intune.
                    </p>
                </div>
            </PageHead>

            <Card>
                <FilterBar>
                    <div style={{ flex: 1, minWidth: '240px', position: 'relative' }}>
                        <FieldLabel htmlFor="dev-search">Recherche</FieldLabel>
                        <Input
                            id="dev-search"
                            style={{ width: '100%', marginTop: '6px' }}
                            placeholder="Nom, identifiant Entra ou identifiant Defender..."
                            value={search}
                            onChange={(e) => updateFilter(setSearch)(e.target.value)}
                            onFocus={() => outsideDefender.length > 0 && setShowSuggestions(true)}
                            // Délai au blur : sans lui, le clic sur une suggestion
                            // est annulé par la fermeture de la liste.
                            onBlur={() => setTimeout(() => setShowSuggestions(false), 150)}
                        />
                        {showSuggestions && outsideDefender.length > 0 && (
                            <SuggestionsList>
                                {outsideDefender.map((s) => (
                                    <SuggestionItem key={s.key} onClick={() => openDevice(s.key)}>
                                        {s.entraName || s.defenderName}
                                        <Muted> — {s.os || 'OS inconnu'} · hors Defender</Muted>
                                    </SuggestionItem>
                                ))}
                            </SuggestionsList>
                        )}
                    </div>
                    <div>
                        <FieldLabel htmlFor="dev-health">État</FieldLabel>
                        <Select
                            id="dev-health"
                            style={{ marginTop: '6px' }}
                            value={healthStatus}
                            onChange={(e) => updateFilter(setHealthStatus)(e.target.value)}
                        >
                            <option value="">Tous</option>
                            <option value="Active">Actif</option>
                            <option value="Inactive">Inactif</option>
                            <option value="ImpairedCommunication">Communication dégradée</option>
                            <option value="NoSensorData">Pas de données capteur</option>
                        </Select>
                    </div>
                    <div>
                        <FieldLabel htmlFor="dev-risk">Niveau de risque</FieldLabel>
                        <Select
                            id="dev-risk"
                            style={{ marginTop: '6px' }}
                            value={riskScore}
                            onChange={(e) => updateFilter(setRiskScore)(e.target.value)}
                        >
                            <option value="">Tous</option>
                            <option value="High">Élevé</option>
                            <option value="Medium">Moyen</option>
                            <option value="Low">Faible</option>
                            <option value="None">Aucun</option>
                        </Select>
                    </div>
                    {/* Masqués par défaut, pas supprimés : l'exclusion est une
                        décision Defender qui peut être levée, et 82 des fiches
                        exclues sont encore vues comme actives. */}
                    <label htmlFor="dev-exclus">
                        <input
                            id="dev-exclus"
                            type="checkbox"
                            checked={includeExcluded}
                            onChange={(e) => updateFilter(setIncludeExcluded)(e.target.checked)}
                        />
                        Inclure les appareils exclus du suivi
                    </label>
                </FilterBar>

                {result?.exclusions?.masquees > 0 && (
                    <Notice $tone="accent" style={{ marginTop: '12px' }}>
                        <i className="bx bx-hide" />
                        <span>
                            {result.exclusions.masquees} fiche{result.exclusions.masquees > 1 ? 's' : ''} exclue
                            {result.exclusions.masquees > 1 ? 's' : ''} du suivi Defender
                            {result.exclusions.masquees > 1 ? ' sont masquées' : ' est masquée'} :
                            doublons de migration, postes hors périmètre ou appareils que Defender
                            ne retrouve plus. Ce ne sont pas des postes à surveiller, mais des
                            artefacts d'inventaire — cochez la case ci-dessus pour les voir.
                        </span>
                    </Notice>
                )}

                {outsideDefender.length > 0 && (
                    <Notice $tone="accent" style={{ marginTop: '12px' }}>
                        <i className="bx bx-info-circle" />
                        <span>
                            {outsideDefender.length} appareil{outsideDefender.length > 1 ? 's' : ''} correspondant
                            à cette recherche {outsideDefender.length > 1 ? 'sont présents' : 'est présent'} dans
                            Entra mais pas dans Defender, donc absent{outsideDefender.length > 1 ? 's' : ''} du
                            tableau. {outsideDefender.length > 1 ? 'Ils sont proposés' : 'Il est proposé'} dans
                            la liste du champ de recherche.
                        </span>
                    </Notice>
                )}
            </Card>

            <Card $pad="16px">
                {error ? (
                    <Notice $tone="danger"><i className="bx bx-error-circle" />{error}</Notice>
                ) : loading && !result ? (
                    <SpinnerContainer><Spinner /></SpinnerContainer>
                ) : result?.data.length === 0 ? (
                    <EmptyState>
                        <i className="bx bx-devices" />
                        <h3>Aucun appareil onboardé</h3>
                        <p>
                            Aucun appareil suivi par Defender ne correspond à ces critères.
                            {outsideDefender.length > 0 && " Des correspondances existent hors Defender, voir ci-dessus."}
                        </p>
                    </EmptyState>
                ) : (
                    <>
                        <TableScroll>
                            <DataTable>
                                <thead>
                                    <tr>
                                        <th>Appareil</th>
                                        <th>Inventaires</th>
                                        <th>Plateforme</th>
                                        <th>État</th>
                                        <th>Risque</th>
                                        <th>Exposition</th>
                                        <th>Critiques</th>
                                        <th>Hautes</th>
                                        <th>Total CVE</th>
                                        <th>Groupe</th>
                                        <th>Vu le</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {result.data.map((device) => (
                                        <ClickableRow key={device.id} onClick={() => openDevice(device.id)}>
                                            <td>
                                                {device.computerDnsName || <Muted>Sans nom</Muted>}
                                                {/* Visible seulement quand on a demandé à les
                                                    inclure : sinon le badge serait toujours absent
                                                    et occuperait de la place pour rien. */}
                                                {device.isExcluded && (
                                                    <Pill
                                                        $tone="neutral"
                                                        style={{ marginLeft: '8px' }}
                                                        title={`Exclue du suivi Defender — motif : ${device.exclusionReason || 'non précisé'}`}
                                                    >
                                                        Exclue
                                                    </Pill>
                                                )}
                                            </td>
                                            <td><SourceBadges sources={device.sources} /></td>
                                            <td><Muted>{device.osPlatform || '—'}</Muted></td>
                                            <td>
                                                <Pill $tone={HEALTH_TONE[device.healthStatus] || 'neutral'}>
                                                    <PillDot />
                                                    {device.healthStatus || '—'}
                                                </Pill>
                                            </td>
                                            <td>
                                                <Pill $tone={RISK_TONE[device.riskScore] || 'neutral'}>
                                                    {device.riskScore || '—'}
                                                </Pill>
                                            </td>
                                            <td><Muted>{device.exposureLevel || '—'}</Muted></td>
                                            <td>{device.criticalCount || <Muted>0</Muted>}</td>
                                            <td>{device.highCount || <Muted>0</Muted>}</td>
                                            <td>{device.cveCount.toLocaleString('fr-FR')}</td>
                                            <td><Muted>{device.rbacGroupName || '—'}</Muted></td>
                                            <td><Muted>{formatDate(device.lastSeen)}</Muted></td>
                                        </ClickableRow>
                                    ))}
                                </tbody>
                            </DataTable>
                        </TableScroll>

                        {pagination && (
                            <Pager>
                                <span>
                                    <strong>{pagination.totalCount.toLocaleString('fr-FR')}</strong> appareils
                                </span>
                                <div className="spacer" />
                                <span>Page <strong>{pagination.page}</strong> / {pagination.totalPages}</span>
                                <IconButton
                                    $bordered
                                    disabled={!pagination.hasPrevPage || loading}
                                    onClick={() => setPage((p) => p - 1)}
                                    title="Page précédente"
                                >
                                    <i className="bx bx-chevron-left" />
                                </IconButton>
                                <IconButton
                                    $bordered
                                    disabled={!pagination.hasNextPage || loading}
                                    onClick={() => setPage((p) => p + 1)}
                                    title="Page suivante"
                                >
                                    <i className="bx bx-chevron-right" />
                                </IconButton>
                            </Pager>
                        )}
                    </>
                )}
            </Card>
        </>
    );
}
